import React, { useRef, useState, useEffect } from 'react';
import { FOCUS_FIELD } from 'Components/DesignSystem/focusRing';

/*
  Re-inked onto role tokens 2026-09-09 (user screenshot in dark: white boxes, retired
  Atlassian chip blue). DARK-MODE COMPATIBILITY ONLY — markup, handlers, testids, the
  MAX_VISIBLE_CHIPS overflow, the outside-click close and the search filter are
  untouched; this is not a DS Tag/Combobox migration (the Tag docblock records that
  this component is where Tag's shape came from). What moved: the field chrome
  (white → surface, #CED4DA → the § 31 edge-strong control tier), the chips (#DEEBFF /
  #0747A6 → the user-pinned chip blue, tone-info tint + ink, § 216), the remove × hover
  (red-600 → tone-danger-glyph, Tag's grammar), the overflow link (accent), the panel
  (surface-raised + edge + the ds-md popover elevation), option hover (the neutral
  hover ground, § 51), the native checkbox's accent colour, and the two greys.
*/

interface Option {
  label: string;
  value: string | number;
  id?: string;
  attributeid?: string;
}

interface Props {
  options: Option[];
  selectedOptions: Option[];
  callback: (state: { val: Option[]; controlid: string }) => void;
  controlid: string;
}

const MAX_VISIBLE_CHIPS = 2;

const AttributeMultiSelectChips: React.FC<Props> = ({
  options,
  selectedOptions,
  callback,
  controlid,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const selected: Option[] = selectedOptions || [];

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (option: Option) => {
    const isSelected = selected.some((s) => s.value === option.value);
    const newSelected = isSelected
      ? selected.filter((s) => s.value !== option.value)
      : [...selected, option];
    callback({ val: newSelected, controlid });
  };

  const removeChip = (option: Option, e: React.MouseEvent) => {
    e.stopPropagation();
    const newSelected = selected.filter((s) => s.value !== option.value);
    callback({ val: newSelected, controlid });
  };

  const filteredOptions = options.filter((o) =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  const visibleChips = selected.slice(0, MAX_VISIBLE_CHIPS);
  const overflowCount = selected.length - MAX_VISIBLE_CHIPS;

  return (
    <div className="tw-relative" ref={containerRef}>
      {/* Chip display area */}
      <div role="button" data-testid="components-attributemultiselectchips-div-1"
        className="tw-flex tw-flex-wrap tw-gap-1 tw-items-center tw-min-h-[42px] tw-border tw-border-edge-strong tw-rounded tw-px-2 tw-py-1 tw-cursor-pointer tw-min-w-[400px] tw-max-w-[400px] tw-bg-surface tw-text-ink"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {selected.length === 0 && (
          <span className="tw-text-ink-secondary tw-text-sm tw-select-none">Select...</span>
        )}
        {visibleChips.map((chip) => (
          <span
            key={chip.value}
            className="tw-flex tw-items-center tw-gap-1 tw-bg-tone-info-tint tw-text-tone-info-ink tw-text-xs tw-px-2 tw-py-0.5 tw-rounded-full tw-max-w-[160px]"
          >
            <span className="tw-truncate tw-max-w-[130px]" title={chip.label}>
              {chip.label}
            </span>
            <button data-testid="components-attributemultiselectchips-button-1"
              type="button"
              onClick={(e) => removeChip(chip, e)}
              className="tw-text-tone-info-ink hover:tw-text-tone-danger-glyph tw-flex-shrink-0 tw-leading-none"
              aria-label={`Remove ${chip.label}`}
            >
              ×
            </button>
          </span>
        ))}
        {overflowCount > 0 && (
          <span role="button" data-testid="components-attributemultiselectchips-span-2"
            className="tw-text-xs tw-text-accent tw-font-medium tw-cursor-pointer hover:tw-underline"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(true);
            }}
          >
            +{overflowCount} more
          </span>
        )}
      </div>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="tw-absolute tw-top-full tw-left-0 tw-z-50 tw-w-full tw-min-w-[400px] tw-bg-surface-raised tw-border tw-border-edge tw-rounded tw-shadow-ds-md tw-mt-1 tw-text-ink">
          <div className="tw-p-2 tw-border-b tw-border-edge">
            <input
              type="text"
              data-testid="product-attribute-chips-search-input"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              className={`tw-w-full tw-border tw-border-edge-strong tw-rounded tw-px-2 tw-py-1 tw-text-sm tw-bg-surface tw-text-ink placeholder:tw-text-ink-secondary ${FOCUS_FIELD}`}
              autoFocus
            />
          </div>
          <div className="tw-max-h-56 tw-overflow-y-auto">
            {filteredOptions.map((option) => {
              const isSelected = selected.some((s) => s.value === option.value);
              return (
                <div role="button" data-testid="components-attributemultiselectchips-div-3"
                  key={option.value}
                  className="tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 hover:tw-bg-surface-hover-strong tw-cursor-pointer tw-text-sm tw-text-ink"
                  onClick={() => toggleOption(option)}
                >
                  <input data-testid="components-attributemultiselectchips-input-2"
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => {}}
                    className="tw-pointer-events-none tw-accent-accent-bold"
                  />
                  <span>{option.label}</span>
                </div>
              );
            })}
            {filteredOptions.length === 0 && (
              <div className="tw-text-sm tw-text-ink-tertiary tw-px-3 tw-py-2">
                No options found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttributeMultiSelectChips;
